import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class AudioUtils {
  constructor() {}

  /**
   * Converts raw PCM audio data into an `AudioBuffer`
   */
  createAudioBuffer(
    audioContext: AudioContext,
    data: ArrayBuffer,
    sampleRate: number = 24000
  ): AudioBuffer | null {
    const audioDataView = new Int16Array(data);
    if (audioDataView.length === 0) {
      console.error('Received audio data is empty.');
      return null;
    }

    const buffer = audioContext.createBuffer(
      1,
      audioDataView.length,
      sampleRate
    );
    const channelData = buffer.getChannelData(0);

    // Convert linear16 PCM to float [-1, 1]
    for (let i = 0; i < audioDataView.length; i++) {
      channelData[i] = audioDataView[i] / 32768;
    }

    return buffer;
  }

  /**
   * Plays an `AudioBuffer` using the Web Audio API
   */
  playAudioBuffer(
    audioContext: AudioContext,
    buffer: AudioBuffer,
    startTimeRef: { current: number },
    analyzer?: AnalyserNode
  ): AudioBufferSourceNode {
    const source = audioContext.createBufferSource();
    source.buffer = buffer;

    if (analyzer) {
      source.connect(analyzer); // ✅ Only connect if analyzer exists
      analyzer.connect(audioContext.destination);
    } else {
      source.connect(audioContext.destination); // ✅ Fallback to direct connection
    }

    const currentTime = audioContext.currentTime;
    if (startTimeRef.current < currentTime) {
      startTimeRef.current = currentTime;
    }

    source.start(startTimeRef.current);
    startTimeRef.current += buffer.duration;

    return source; // ✅ Return the source node
  }

  /**
   * Downsamples an audio buffer to a lower sample rate
   */
  downsample(
    buffer: Float32Array,
    fromSampleRate: number,
    toSampleRate: number
  ): Float32Array {
    if (fromSampleRate === toSampleRate) {
      return buffer;
    }

    const sampleRateRatio = fromSampleRate / toSampleRate;
    const newLength = Math.round(buffer.length / sampleRateRatio);
    const result = new Float32Array(newLength);

    let offsetResult = 0;
    let offsetBuffer = 0;

    while (offsetResult < result.length) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
      let accum = 0,
        count = 0;

      for (
        let i = offsetBuffer;
        i < nextOffsetBuffer && i < buffer.length;
        i++
      ) {
        accum += buffer[i];
        count++;
      }

      result[offsetResult] = accum / count;
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }

    return result;
  }

  /**
   * Converts a Float32Array to an Int16Array
   */
  convertFloat32ToInt16(buffer: Float32Array): ArrayBuffer {
    const length = buffer.length;
    const int16Buffer = new Int16Array(length);

    for (let i = 0; i < length; i++) {
      int16Buffer[i] = Math.min(1, buffer[i]) * 0x7fff;
    }

    return int16Buffer.buffer;
  }

  /**
   * Normalizes the volume level based on the `AnalyserNode`
   */
  normalizeVolume(
    analyzer: AnalyserNode,
    dataArray: Uint8Array,
    normalizationFactor: number
  ): number {
    analyzer.getByteFrequencyData(dataArray);
    const sum = dataArray.reduce((acc, val) => acc + val, 0);
    const average = sum / dataArray.length;
    return Math.min(average / normalizationFactor, 1);
  }
}
